import React, { useState, useEffect } from 'react';
import { Loader2, Save, XCircle, Tag, FileText, AlertTriangle } from 'lucide-react';

export default function PositionForm({ position, onSave, onCancel, isNew, submitting: propSubmitting }) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true,
    });
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (position) {
            setFormData({
                id: position.id,
                name: position.name || '',
                description: position.description || '',
                isActive: position.isActive ?? true,
            });
        } else {
            setFormData({
                name: '',
                description: '',
                isActive: true,
            });
        }
        setFormError(null);
    }, [position, isNew]);

    const currentSubmittingState = propSubmitting || submitting;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        const trimmedName = (formData.name || '').trim();
        if (!trimmedName) {
            setFormError("Pozisyon adı boş bırakılamaz.");
            return;
        }

        setSubmitting(true);
        try {
            await onSave({
                ...formData,
                name: trimmedName,
                description: (formData.description || '').trim()
            });
        } catch (err) {
            setFormError(err.message || "Kaydetme işlemi sırasında bir hata oluştu.");
        } finally {
            setSubmitting(false);
        }
    };

    const submitButtonText = isNew ? "Pozisyon Ekle" : "Değişiklikleri Kaydet";
    const activeEmpCount = position?.activeEmployeeCount || 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3 rounded-xl text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                    <span>{formError}</span>
                </div>
            )}

            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
                    <Tag className="w-4 h-4 mr-1.5 text-indigo-500" />
                    Pozisyon / Ünvan Adı <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="Örn: Kıdemli Yazılım Uzmanı, Üretim Operatörü, İK Yöneticisi"
                    disabled={currentSubmittingState}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    required
                />
            </div>

            <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
                    <FileText className="w-4 h-4 mr-1.5 text-indigo-500" />
                    Açıklama
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={500}
                    rows="3"
                    placeholder="Pozisyon görev tanımı veya kıdem notu (isteğe bağlı)..."
                    disabled={currentSubmittingState}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                />
            </div>

            <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                    <label htmlFor="isActive" className="text-sm font-semibold text-gray-800 dark:text-gray-200 cursor-pointer">
                        Pozisyon Durumu (Aktif)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Pasif pozisyonlar yeni personel atamalarında ve Excel şablonlarında listelenmez.
                    </p>
                </div>
                <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={currentSubmittingState}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                />
            </div>

            {!isNew && !formData.isActive && activeEmpCount > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-3 rounded-xl text-xs flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                    <span>
                        Bu pozisyonda halen <strong>{activeEmpCount} aktif personel</strong> çalışmaktadır. Pozisyonu pasife aldığınızda mevcut personellerin ünvanı korunur ancak bu pozisyona yeni atama yapılamaz.
                    </span>
                </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-xl transition"
                    disabled={currentSubmittingState}
                >
                    <XCircle className="w-4 h-4 mr-1.5" />
                    İptal
                </button>
                <button
                    type="submit"
                    className="flex items-center px-5 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white rounded-xl shadow-sm transition"
                    disabled={currentSubmittingState}
                >
                    {currentSubmittingState ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            Kaydediliyor...
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-1.5" />
                            {submitButtonText}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
